'use client';

import { GroupPresenceCluster } from '@/components/chat/GroupPresenceCluster';
import { SharePopover } from '@/components/ui/share-popover';
import { useRouteKind } from '@/hooks/useRouteKind';
import { useAppSelector } from '@/lib/redux/hooks';
import { cn } from '@/lib/utils';
import { Share2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams } from 'next/navigation';
import type React from 'react';

type ShareButtonProps = {
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  url?: string;
  title?: string;
  disabled?: boolean;
  iconOnly?: boolean;
};

export default function ShareButton({
  className,
  onClick,
  url,
  title,
  disabled,
  iconOnly,
}: ShareButtonProps) {
  const t = useTranslations('SharePopover');
  // Hide on share pages so a viewer can't "re-share" an already-shared session.
  // Routed through the single source of truth (#5895): the previous inline check
  // compared `pathname` against un-interpolated route-TEMPLATE strings
  // (`/chat/share/[sessionId]`), which a real share URL (`/chat/share/abc-123`)
  // never equals — so the early-return was dead and the button rendered on share
  // pages. `isSharePage` is a `/share/` substring match, locale-prefix agnostic.
  const { isSharePage } = useRouteKind();
  // Group chat is whatever the chat session published to the store — this
  // component derives nothing itself, so it can render in any header layout.
  const groupSessionId = useAppSelector(
    (state) => state.groupPresence.sessionId
  );
  const params = useParams();
  const sessionIdParam = params?.['sessionId'];
  const sessionId = Array.isArray(sessionIdParam)
    ? sessionIdParam[0]
    : (sessionIdParam as string | undefined);

  if (!sessionId) return null;
  if (isSharePage) {
    return null;
  }

  // Only merge when the published presence belongs to THIS session — guards the
  // window after navigating away, before the chat page clears the slice.
  const isGroup = groupSessionId === sessionId;

  const shareButton = (
    <SharePopover sessionId={sessionId} url={url} title={title}>
      <button
        type='button'
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          if (onClick) onClick(event);
        }}
        className={cn(
          'flex items-center justify-center text-sm',
          // In a group chat the button sits INSIDE the shared pill, so it drops
          // its own border/background/shadow — otherwise the merged control
          // reads as two nested pills instead of one.
          isGroup
            ? 'rounded-full px-3 py-1 hover:bg-interactive-hover'
            : 'bg-surface-elevated hover:bg-interactive-hover border border-line-default rounded-full px-4 py-2 shadow-sm',
          'transition-colors duration-200',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          className
        )}
      >
        <Share2 className={cn('h-4 w-4', !iconOnly && 'mr-2')} />
        {!iconOnly && t('share')}
      </button>
    </SharePopover>
  );

  if (!isGroup) {
    return shareButton;
  }

  // Group chat: collaborators and Share are ONE control (the Miro treatment) —
  // avatars, @Lex, members, then Share, inside a single pill. Presence comes
  // from the `groupPresence` slice, so this component needs no session context
  // of its own and stays correct in every header layout that renders it.
  return (
    <div className='flex items-center gap-1.5 rounded-full border border-line-default bg-surface-elevated py-1 pl-2 pr-1 shadow-sm'>
      <GroupPresenceCluster bare />
      <div className='h-5 w-px bg-line-subtle' aria-hidden='true' />
      {shareButton}
    </div>
  );
}
